<template>
    <button
        type="button"
        class="save-for-later-button"
        :class="classSaveForLaterButton"
        :aria-label
        @click.stop.prevent="toggleSaveForLater"
    >
        <Icon
            v-if="!isSaved"
            icon="heart"
        />

        <div
            v-else
            class="filled"
        >
            <Icon
                class="icon red"
                icon="heart-filled"
            />
            <Icon
                class="icon animation"
                icon="heart-filled"
            />
        </div>

        <div
            v-if="belowCta"
            class="label"
        >
            Add to wishlist
        </div>
    </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
    type SaveabledProduct,
    type SaveForLaterPageType,
    useSaveProductButton,
} from '@/components/saved-products/useSavedProducts'
import { useSegment } from '@/third-party/segment.ts'
import { getWindow } from '@/helpers/globals.ts'

import Icon from '@/components/ui/icon/Icon.vue'

const { product, pageType, belowCta } = defineProps<{
    product: SaveabledProduct | undefined
    pageType: SaveForLaterPageType
    belowCta?: boolean
}>()

const { addProduct, removeProduct, getIsProductSaved } = useSaveProductButton()

const isSaved = computed(() => product && getIsProductSaved(product))

const { track } = useSegment()

const toggleSaveForLater = () => {
    if (!product) return

    let eventName = 'save_product'

    if (isSaved.value) {
        eventName = 'remove_saved_product'
        removeProduct(product)
    } else {
        addProduct(product)
    }

    const url = getWindow()?.location.href

    if (!url) {
        return
    }

    const eventSuffix = pageType
    const belowCtaSuffix = belowCta ? '_below_cta' : ''

    track(`custom_${eventName}_${eventSuffix}${belowCtaSuffix}`, {
        param1: 'Save for later',
        param2: eventName,
        param3: `${eventSuffix} icon click${belowCtaSuffix.replace('_', ' ')}`,
        param4: url,
        url,
        eventName,
    })
}

const ariaLabel = computed(() => `Save ${product?.productName || ''} for later`)

const classSaveForLaterButton = computed(() => ({
    saved: isSaved.value,
    'below-cta': belowCta,
}))
</script>

<style lang="scss" scoped>
.save-for-later-button {
    display: block;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    cursor: pointer;
    font-size: 20px;
    text-align: center;
    line-height: 40px;
    border-radius: 3px;
    border: none;
    position: relative;

    @include hover() {
        font-size: 22px;
    }

    &.saved {
        font-size: 22px;
    }

    &.below-cta {
        width: 100%;
        margin-top: 16px;
        height: 47px;
        border: 1px solid $blue;
        border-radius: 32px;
        display: flex;
        justify-content: center;
        align-items: center;

        cursor: pointer;

        .label {
            margin-left: 8px;
            font-family: $effra-font-stack;
            color: $blue;
            font-weight: 500;
            font-size: 0.875rem;
            line-height: 1.313rem;
            letter-spacing: 0.05em;
            text-transform: uppercase;
        }
    }

    .red {
        color: $red;
    }

    .filled {
        display: block;
        position: relative;

        .animation {
            display: block;
            font-size: 20px;
            color: $red;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            animation: heartAnimation 0.4s ease-out forwards;
        }
    }
}

@keyframes heartAnimation {
    0% {
        transform: scale(1);
        opacity: 1;
    }

    100% {
        transform: scale(1.8);
        opacity: 0;
        top: 2px;
    }
}
</style>
